/** Client-side Order build trace — mirrors API build_order_build_trace. */

const LEAF_FORECAST_METHOD = "leaf_forecast";

function indexWeekOrderLines(weekPayload) {
  const byItem = {};
  for (const day of weekPayload?.days || []) {
    const orderDate = day?.order_date;
    for (const supplier of day?.suppliers || []) {
      const supplierName = supplier?.supplier_name;
      const supplierId = supplier?.supplier_id;
      for (const line of supplier?.lines || []) {
        const itemId = String(line?.supy_item_id || "").trim();
        const productId = line?.product_id;
        const keys = [];
        if (itemId) keys.push(`item:${itemId}`);
        if (productId != null && productId !== "") keys.push(`product:${Number(productId)}`);
        const snippet = {
          order_date: orderDate,
          supplier_id: supplierId,
          supplier_name: supplierName,
          product_id: productId,
          product_name: line?.product_name || line?.item_name,
          estimated_stock: line?.estimated_stock,
          stock_source: line?.stock_source,
          base_need_qty: line?.base_need_qty,
          suggested_order_qty: line?.suggested_order_qty,
          line_total: line?.line_total,
          predicted_daily_qty: line?.predicted_daily_qty,
          method_used: line?.method_used,
          needs_stock_count: line?.needs_stock_count,
        };
        keys.forEach((key) => {
          if (!byItem[key]) byItem[key] = [];
          byItem[key].push(snippet);
        });
      }
    }
  }
  return byItem;
}

function dropReasonsForName(drops, productName) {
  const name = String(productName || "").trim();
  if (!name) return [];
  const reasons = [];
  const samples = drops?.samples || {};
  Object.entries(samples).forEach(([reason, names]) => {
    for (const sample of names || []) {
      const s = String(sample).trim();
      // Match API: exact, or product name is a substring of the sample label.
      if (s === name || String(sample).includes(name)) {
        reasons.push(String(reason));
        break;
      }
    }
  });
  return reasons;
}

/**
 * Explain each published leaf item vs week-orders outcome.
 * @param {object} leavesPayload from GET /forecast-trace/leaves
 * @param {object} weekPayload from GET /week-orders
 */
export function buildOrderBuildTrace(leavesPayload, weekPayload) {
  const orderedIndex = indexWeekOrderLines(weekPayload || {});
  const nameToOrdered = {};
  for (const day of weekPayload?.days || []) {
    for (const supplier of day?.suppliers || []) {
      for (const line of supplier?.lines || []) {
        const pname = String(line?.product_name || line?.item_name || "").trim();
        if (!pname) continue;
        if (!nameToOrdered[pname]) nameToOrdered[pname] = [];
        nameToOrdered[pname].push({
          order_date: day?.order_date,
          supplier_id: supplier?.supplier_id,
          supplier_name: supplier?.supplier_name,
          product_id: line?.product_id,
          product_name: pname,
          estimated_stock: line?.estimated_stock,
          stock_source: line?.stock_source,
          base_need_qty: line?.base_need_qty,
          suggested_order_qty: line?.suggested_order_qty,
          line_total: line?.line_total,
          predicted_daily_qty: line?.predicted_daily_qty,
          method_used: line?.method_used,
        });
      }
    }
  }

  const rawWeekDrops = weekPayload?.drops || { total: 0, counts: {}, samples: {} };
  // Prefer unique item_ids per reason (week API used to sum 7 weekdays).
  const weekDrops = { ...rawWeekDrops };
  const itemIdsByReason = rawWeekDrops.item_ids || {};
  if (itemIdsByReason && typeof itemIdsByReason === "object") {
    const counts = { ...(rawWeekDrops.counts || {}) };
    Object.entries(itemIdsByReason).forEach(([reason, ids]) => {
      counts[reason] = Array.isArray(ids) ? ids.length : Number(counts[reason] || 0);
    });
    weekDrops.counts = counts;
    weekDrops.total = Object.values(counts).reduce((s, n) => s + (Number(n) || 0), 0);
  }
  const items = [];
  const outcomeCounts = {};

  for (const leaf of leavesPayload?.lines || []) {
    const itemId = String(leaf?.item_id || "");
    const productId = leaf?.product_id;
    const productName = String(leaf?.product_name || itemId);
    let outcome;
    let orderDays = [];

    const leafIssue = String(leaf?.order_issue || "").trim() || null;
    if (leaf?.excluded) {
      outcome = leafIssue || "excluded_config";
    } else {
      orderDays = [...(orderedIndex[`item:${itemId}`] || [])];
      if (!orderDays.length && productId != null && productId !== "") {
        orderDays = [...(orderedIndex[`product:${Number(productId)}`] || [])];
      }
      if (!orderDays.length) {
        orderDays = [...(nameToOrdered[productName] || [])];
      }

      const dropReasons = dropReasonsForName(weekDrops, productName);
      if (orderDays.length) {
        const totalSuggested = orderDays.reduce(
          (s, d) => s + Number(d.suggested_order_qty || 0),
          0
        );
        const totalNeed = orderDays.reduce((s, d) => s + Number(d.base_need_qty || 0), 0);
        outcome = totalSuggested <= 0 && totalNeed <= 0 ? "covered_by_stock" : "ordered";
      } else if (leafIssue) {
        outcome = leafIssue;
      } else if (!leaf?.mapped_to_product) {
        outcome = "no_catalog_product";
      } else if (dropReasons.length) {
        outcome = dropReasons[0] || "not_in_week_basket";
      } else {
        outcome = "not_in_week_basket";
      }
    }

    outcomeCounts[outcome] = (outcomeCounts[outcome] || 0) + 1;
    const stock0 = orderDays.length ? orderDays[0].estimated_stock : null;
    const stockSource = orderDays.length ? orderDays[0].stock_source : null;
    items.push({
      item_id: itemId,
      retailer_item_id: leaf?.retailer_item_id || null,
      product_id: productId,
      product_name: productName,
      supplier_name: leaf?.supplier_name,
      channel_supplier_names: leaf?.channel_supplier_names || [],
      qty_hat_base: leaf?.qty_hat_base,
      predicted_daily: leaf?.predicted_daily,
      catalog_eur_estimate: leaf?.catalog_eur_estimate,
      estimated_stock: stock0,
      stock_source: stockSource,
      order_days: orderDays,
      suggested_order_qty_week: Math.round(
        orderDays.reduce((s, d) => s + Number(d.suggested_order_qty || 0), 0) * 10000
      ) / 10000,
      base_need_qty_week: Math.round(
        orderDays.reduce((s, d) => s + Number(d.base_need_qty || 0), 0) * 10000
      ) / 10000,
      line_total_eur_week: Math.round(
        orderDays.reduce((s, d) => s + Number(d.line_total || 0), 0) * 100
      ) / 100,
      outcome,
      order_issue: leaf?.order_issue || null,
      order_issue_label: leaf?.order_issue_label || null,
      order_issue_fix_hint: leaf?.order_issue_fix_hint || null,
      item_status: leaf?.item_status ?? null,
      drop_reasons: dropReasonsForName(weekDrops, productName),
      is_chicken_match: !!leaf?.is_chicken_match,
      method_used: LEAF_FORECAST_METHOD,
    });
    // Pass through only when leaves API included the field (avoid false “none”).
    if (Object.prototype.hasOwnProperty.call(leaf || {}, "last_ledger_activity")) {
      const last = items[items.length - 1];
      last.last_ledger_activity =
        leaf.last_ledger_activity != null && leaf.last_ledger_activity !== ""
          ? String(leaf.last_ledger_activity).slice(0, 10)
          : null;
      last.last_ledger_activity_days_ago =
        leaf.last_ledger_activity_days_ago != null &&
        Number.isFinite(Number(leaf.last_ledger_activity_days_ago))
          ? Number(leaf.last_ledger_activity_days_ago)
          : null;
    }
  }

  items.sort((a, b) => {
    const ac = a.is_chicken_match ? 0 : 1;
    const bc = b.is_chicken_match ? 0 : 1;
    if (ac !== bc) return ac - bc;
    const ao = a.outcome !== "ordered" ? 0 : 1;
    const bo = b.outcome !== "ordered" ? 0 : 1;
    if (ao !== bo) return ao - bo;
    const aq = Number(b.qty_hat_base || 0) - Number(a.qty_hat_base || 0);
    if (aq !== 0) return aq;
    return String(a.product_name || "").localeCompare(String(b.product_name || ""));
  });

  let weekBasket = weekPayload?.week_basket_eur;
  if (weekBasket == null) {
    weekBasket =
      Math.round(
        (weekPayload?.days || []).reduce((s, d) => s + Number(d.basket_eur || 0), 0) * 100
      ) / 100;
  }

  return {
    location_id: weekPayload?.location_id || leavesPayload?.location_id,
    location_name: weekPayload?.location_name,
    brand_id: leavesPayload?.brand_id,
    week_start: weekPayload?.week_start || leavesPayload?.week_start,
    week_end: weekPayload?.week_end || leavesPayload?.week_end,
    week_basket_eur: weekBasket,
    catalog_eur_estimate_total: leavesPayload?.catalog_eur_estimate_total,
    catalog_eur_note: leavesPayload?.catalog_eur_note,
    is_leaf_forecast_week: !!weekPayload?.is_leaf_forecast_week,
    method_used_counts: weekPayload?.method_used_counts || {},
    drops: weekDrops,
    outcome_counts: outcomeCounts,
    items,
    item_count: items.length,
    chicken_item_count: items.filter((x) => x.is_chicken_match).length,
  };
}
